@if (!active()) {
    <pf-text type="description"
        >Shipping details will appear once you reach this step.
    </pf-text>
} @else if (!hasAnyPackages()) {
    <pf-text type="description"
        >Add packages to generate shipping details.
    </pf-text>
} @else if (shipmentDetails(); as shippingDetails) {
    <article pfArticle [pfTitle]="title() || 'Shipping Details'">
        <div id="shipment-chips">
            @if (isHazardous()) {
                <pf-chip icon="warning" pfStatus="warning">Hazmat</pf-chip>
            }
            @if (isWillCall()) {
                <pf-chip icon="error" pfStatus="danger">Will Call</pf-chip>
            }
            @if (isHotShot()) {
                <pf-chip icon="error" pfStatus="danger">Hot Shot</pf-chip>
            }
        </div>
        <div class="shipping-details">
            @if (shippingDetails.requestor) {
                <div class="row">
                    <div class="label">Requestor</div>
                    <div class="value">{{ shippingDetails.requestor }}</div>
                </div>
            }
            @if (shippingDetails.shipTo) {
                <div class="row">
                    <div class="label">Ship To</div>
                    <div class="value ship-to-address">
                        <div>{{ shippingDetails.shipTo.name }}</div>
                        <div>{{ shippingDetails.shipTo.addressLine1 }}</div>
                        @if (shippingDetails.shipTo.addressLine2) {
                            <div>{{ shippingDetails.shipTo.addressLine2 }}</div>
                        }
                        <div>
                            {{ shippingDetails.shipTo.city }} ,
                            {{ shippingDetails.shipTo.geoZone }}
                            {{ shippingDetails.shipTo.postalCode }}
                        </div>
                    </div>
                </div>
            }
            @for (label of shippingDetails.packages; track $index) {
                <div>Package {{ $index + 1 }}</div>
                <div class="shipping-label">
                    @if (label.contentItems && label.contentItems.length > 0) {
                        <div class="row">
                            <div class="label">Contents</div>
                            <div class="value">
                                @if (
                                    label.totalQuantity &&
                                    label.totalQuantity > 1
                                ) {
                                    <div class="total-quantity">
                                        {{ label.totalQuantity }} total items:
                                    </div>
                                }
                                <div class="content-items">
                                    @for (
                                        item of label.contentItems;
                                        track item.description
                                    ) {
                                        <div class="content-item">
                                            <span class="quantity"
                                                >{{ item.quantity }} x</span
                                            >
                                            <span class="description">{{
                                                item.description
                                            }}</span>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                    @if (label.weight) {
                        <div class="row">
                            <div class="label">Weight</div>
                            <div class="value">{{ label.weight }}</div>
                        </div>
                    }
                    @if (label.box) {
                        <div class="row">
                            <div class="label">Box</div>
                            <div class="value">{{ label.box }}</div>
                        </div>
                    } @else if (label.dimensions) {
                        <div class="row">
                            <div class="label">Dimensions</div>
                            <div class="value">{{ label.dimensions }}</div>
                        </div>
                    }
                    @if (label.value) {
                        <div class="row">
                            <div class="label">Value</div>
                            <div class="value">{{ label.value }}</div>
                        </div>
                    }
                </div>
            }
            @if (shippingDetails.service) {
                <div class="row">
                    <div class="label">Service</div>
                    <div class="value">{{ shippingDetails.service }}</div>
                </div>
            }
            @if (shippingDetails.speed) {
                <div class="row">
                    <div class="label">Speed</div>
                    <div class="value">{{ shippingDetails.speed }}</div>
                </div>
            }
            @if (shippingDetails.trackingNumber) {
                <div class="row">
                    <div class="label">Tracking</div>
                    <div class="value">
                        <a
                            href="https://www.fedex.com/fedextrack/?trknbr={{
                                shippingDetails.trackingNumber
                            }}"
                            >{{ shippingDetails.trackingNumber }}</a
                        >
                    </div>
                </div>
            }

            @if (!readyToShip()) {
                <div id="confirm-shipment">
                    @if (
                        allowManualTracking() &&
                        !isHotShot() &&
                        !isWillCall() &&
                        showManualTracking()
                    ) {
                        <div class="manual-tracking">
                            <pf-text type="info">
                                Already have a shipping label? Scan or type the
                                tracking number and press Enter to save it and
                                skip label generation.
                            </pf-text>
                            <cpw-scan-input
                                label="Tracking number"
                                [autoFocus]="true"
                                [disabled]="disabled() || generating()"
                                (barcodeScanned)="
                                    onManualTrackingEntered($event)
                                ">
                            </cpw-scan-input>
                            @if (generating()) {
                                <pf-text type="info"
                                    >Saving tracking number…
                                </pf-text>
                            }
                            <pf-button
                                buttonType="cancel"
                                block
                                [disabled]="disabled() || generating()"
                                (click)="
                                    showManualTracking.set(false);
                                    manualTrackingNumber.set('')
                                ">
                                Cancel
                            </pf-button>
                        </div>
                    } @else {
                        @if (useFedEx()) {
                            <pf-text type="info" class="confirm-shipment__hint"
                                >Review the packing list and ensure all packages
                                are correctly packed before generating shipping
                                labels.
                            </pf-text>
                        }
                        <div class="shipment-options">
                            @if (
                                allowManualTracking() &&
                                !isHotShot() &&
                                !isWillCall()
                            ) {
                                <button
                                    type="button"
                                    class="shipment-option"
                                    [disabled]="disabled()"
                                    (click)="showManualTracking.set(true)">
                                    <span class="shipment-option__title"
                                        >Enter Tracking Number</span
                                    >
                                    <span class="shipment-option__description"
                                        >Handled out of system and can be
                                        completed by entering tracking
                                        number.</span
                                    >
                                </button>
                            }
                            <button
                                type="button"
                                class="shipment-option"
                                [disabled]="
                                    disabled() ||
                                    generating() ||
                                    !canConfirmShipment()
                                "
                                (click)="generateShippingLabels()">
                                <span class="shipment-option__title">{{
                                    generating()
                                        ? "Generating shipment…"
                                        : "Confirm Shipment"
                                }}</span>
                                <span class="shipment-option__description"
                                    >Generate shipping labels to complete.</span
                                >
                            </button>
                        </div>
                    }
                </div>
            } @else {
                @if (isWillCall()) {
                    <pf-text type="info" class="will-call-pickup-message">
                        Please move these items to the will call area for
                        technician pickup.
                    </pf-text>
                }
                @if (isHotShot()) {
                    <pf-text type="info" class="hot-shot-pickup-message">
                        Please move these items to the hot shot area for pickup.
                    </pf-text>
                }
                <div class="print-actions">
                    @if (shippingLabels()) {
                        <div class="row">
                            <div class="label">
                                Shipping Labels
                                @if (hasPackingSlips()) {
                                    + Packing Slips
                                }
                            </div>
                            <div class="value">
                                <pf-button
                                    buttonType="print-rounded"
                                    [disabled]="shipping() || disabled()"
                                    (click)="onPrintLabels()"
                                    >{{
                                        printedDocuments().labels
                                            ? "Reprint"
                                            : "Print"
                                    }}
                                </pf-button>
                            </div>
                        </div>
                    } @else if (hasPackingSlips()) {
                        <div class="row">
                            <div class="label">Packing Slips</div>
                            <div class="value">
                                <pf-button
                                    buttonType="print-rounded"
                                    [disabled]="shipping() || disabled()"
                                    (click)="onPrintPackingSlips()"
                                    >{{
                                        printedDocuments().packingSlips
                                            ? "Reprint"
                                            : "Print"
                                    }}
                                </pf-button>
                            </div>
                        </div>
                    }

                    @if (billOfLading()) {
                        <div class="row">
                            <div class="label">BOL</div>
                            <div class="value">
                                <pf-button
                                    buttonType="print-rounded"
                                    [disabled]="shipping() || disabled()"
                                    (click)="onPrintBol()"
                                    >{{
                                        printedDocuments().bol
                                            ? "Reprint"
                                            : "Print"
                                    }}
                                </pf-button>
                            </div>
                        </div>
                    }

                    @if (commercialInvoice()) {
                        <div class="row">
                            <div class="label">Customer Invoice</div>
                            <div class="value">
                                <pf-button
                                    buttonType="print-rounded"
                                    [disabled]="shipping() || disabled()"
                                    (click)="onPrintCommercialInvoice()"
                                    >{{
                                        printedDocuments().commercialInvoice
                                            ? "Reprint"
                                            : "Print"
                                    }}
                                </pf-button>
                            </div>
                        </div>
                    }
                </div>
                @if (hasPallets() && !isWillCall() && !isHotShot()) {
                    <div class="dock-scan">
                        <pf-chip [pfStatus]="ltlProcessingChip().status"
                            >{{ ltlProcessingChip().label }}
                        </pf-chip>
                        @if (scannedDockSlot(); as dock) {
                            <pf-chip pfStatus="success"
                                >Dock: {{ dock.barcode }}
                            </pf-chip>
                        } @else {
                            <cpw-scan-input
                                label="Scan dock location"
                                [barcodeSearchFacade]="slotFacade"
                                defaultScanError="The scanned location is not designated for shipping."
                                (barcodeScanned)="onDockSlotScanned($event)">
                            </cpw-scan-input>
                        }
                    </div>
                }
                <pf-button
                    id="finish-button"
                    block
                    buttonType="submit"
                    [disabled]="!canFinish()"
                    [loading]="shipping()"
                    (click)="completeShipment()">
                    {{ shipping() ? "Completing..." : "Finish" }}
                </pf-button>
            }
        </div>
    </article>
}
